import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import useDynamicRecoil from '~/shared/hooks/useDynamicRecoil';
import { Flex, FlexColumn } from '~/shared/components';
import { getQuerystringResolution } from '~/shared/utils';
import { FilterItemProps } from '../types';
import DisplayDate from './DisplayDate';
import DisplayDateResolution from './DisplayDateResolution';
import DisplayDateTitle from './DisplayDateTitle';
import DisplayDateResolutionTitle from './DisplayDateResolutionTitle';
import DatePickerEditor from './DatePickerEditor';

const Wrapper = styled(FlexColumn)`
  position: relative;
  min-width: 240px;
  margin-top: 6px;
`;

const DateWrapper = styled(Flex)`
  position: relative;
  min-width: 240px;
  margin-top: 12px;
  align-items: center;
  color: var(--filter-bar-date-icon-color);

  .MuiIconButton-root svg {
    fill: var(--filter-bar-date-icon-color);
    transition: fill 120ms ease;
  }

  .MuiIconButton-root:hover svg {
    fill: var(--filter-bar-date-icon-hover-fill);
  }
`;

const DateTitle = styled(Flex)`
  font-size: 13px;
  font-weight: 400;
  color: #4c5e72;
  pointer-events: none;
`;

const DatePickerBg = styled('div')<{ hidden: any }>`
  position: fixed;
  left: 0px;
  top: 0px;
  width: 100vw;
  height: 100vh;
  background: transparent;
  z-index: 2;
`;

const DateFilterRange = ({
  name,
  value,
  onChangeTime,
  onChangeTimeResolution,
  dateType,
  options,
  measure,
  timeResolution,
  isTimeResolution,
  filters,
  dateFilter,
  prismId,
}: FilterItemProps) => {
  const location = useLocation();
  const search = useMemo(() => location?.search, [location]);
  const querystringResolution = useMemo(() => getQuerystringResolution(search), [search]);

  const urlResolution = useMemo(() => {
    if (!querystringResolution) return undefined;
    try {
      const decoded = decodeURIComponent(querystringResolution.replace(/\+/g, ' '));
      return decoded.includes('Day') ? decoded : undefined;
    } catch (error) {
      return undefined;
    }
  }, [querystringResolution]);
  const urlDate = useMemo(() => search?.split('?')[1]?.split('&')[0]?.split('=')[1]?.replace(/\+/g, ' '), [search]);

  const [isHidden, setIsHidden] = useState(true);

  const resolutionOptions = useMemo(() => {
    if (filters?.find((f: any) => f.id === 'Formation Period')) {
      return filters?.find((f: any) => f.id === 'Formation Period')?.options;
    }
    if (filters?.find((f: any) => f.id === 'Time Resolution')) {
      return filters?.find((f: any) => f.id === 'Time Resolution')?.options;
    }
  }, [filters]);

  const defaultResolution = useMemo(() => {
    if (filters?.find((f: any) => f.id === 'Formation Period'))
      return filters?.find((f: any) => f.id === 'Formation Period')?.defaultValue;
    if (filters?.find((f: any) => f.id === 'Time Resolution'))
      return filters?.find((f: any) => f.id === 'Time Resolution')?.defaultValue;
  }, [filters]);

  const fallbackRangeId = useMemo(() => {
    if (Array.isArray(resolutionOptions) && resolutionOptions.length > 0) {
      return resolutionOptions[0]?.id ?? 'Trending 7 Days';
    }
    return 'Trending 7 Days';
  }, [resolutionOptions]);

  const [selectedRange, setSelectedRange] = useDynamicRecoil('dateFilterRange', defaultResolution || fallbackRangeId);

  const initialResolution = useMemo(() => {
    if (urlResolution) return urlResolution;
    if (defaultResolution) return defaultResolution;
    return fallbackRangeId || '7-Day';
  }, [urlResolution, defaultResolution, fallbackRangeId]);

  const [selectedResolution, setSelectedResolution] = useDynamicRecoil('dateFilterResolution', initialResolution);
  const [selectedResolutionButton, setSelectedResolutionButton] = useDynamicRecoil(
    'dateFilterResolutionButton',
    initialResolution,
  );

  const normalizedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value[0] ?? '';
    }
    return value ?? '';
  }, [value]);

  const resolutionCondition = useMemo(() => {
    if (urlResolution) return urlResolution;
    if (defaultResolution) return defaultResolution;
    return selectedResolution || fallbackRangeId || '7-Day';
  }, [urlResolution, defaultResolution, fallbackRangeId, selectedResolution]);

  const [selectedDate, setSelectedDate] = useState(
    urlDate && dayjs(urlDate).isValid()
      ? urlDate
      : dayjs(normalizedValue).isValid()
        ? normalizedValue
        : dateFilter.defaultValue,
  );
  const [innerSelectedDate, setInnerSelectedDate] = useState(
    urlDate && dayjs(urlDate).isValid()
      ? urlDate
      : dayjs(normalizedValue).isValid()
        ? normalizedValue
        : dateFilter.defaultValue,
  );

  useEffect(() => {
    if (!urlDate || !dayjs(urlDate).isValid()) return;
    setSelectedDate(urlDate);
    setInnerSelectedDate(urlDate);
  }, [urlDate]);

  const onDateChange = useCallback((props: any) => {
    setInnerSelectedDate(props);
  }, []);

  const handleSave = useCallback(() => {
    const normalizedDate = dayjs(innerSelectedDate).isValid()
      ? dayjs(innerSelectedDate).format('YYYY-MM-DD')
      : dayjs(dateFilter.defaultValue).format('YYYY-MM-DD');

    if (!isTimeResolution && onChangeTime) {
      onChangeTime(normalizedDate, selectedRange);
      setSelectedDate(normalizedDate);
      setInnerSelectedDate(normalizedDate);
      setIsHidden(true);
      return;
    }

    if (isTimeResolution && onChangeTimeResolution) {
      const nextResolution =
        selectedResolutionButton || selectedResolution || resolutionCondition || resolutionOptions?.[0]?.id || '7-Day';

      setSelectedResolution(nextResolution);
      setSelectedResolutionButton(nextResolution);

      if (normalizedDate !== selectedDate || nextResolution !== timeResolution) {
        setSelectedDate(normalizedDate);
        setInnerSelectedDate(normalizedDate);
        onChangeTimeResolution(normalizedDate, nextResolution);
      }
    }

    setIsHidden(true);
  }, [
    innerSelectedDate,
    selectedRange,
    selectedResolution,
    selectedResolutionButton,
    resolutionCondition,
    isTimeResolution,
    onChangeTime,
    onChangeTimeResolution,
    selectedDate,
    timeResolution,
    resolutionOptions,
    dateFilter.defaultValue,
  ]);

  const [oldPrismId, setOldPrismId] = useState(prismId);

  useEffect(() => {
    if (oldPrismId === prismId && !(isTimeResolution && !urlDate)) {
      const hasAbnormalIntensity = !!filters
        .find((f: any) => f.id === 'Measure')
        ?.options.find((o: any) => o.id === 'Abnormal Intensity');
      if (
        isTimeResolution &&
        ((hasAbnormalIntensity && measure !== 'Abnormal Intensity') || !measure) &&
        selectedResolution !== (urlResolution || defaultResolution || resolutionOptions?.[0]?.id)
      ) {
        setSelectedResolution(resolutionCondition);
        setSelectedResolutionButton(resolutionCondition);
        setSelectedRange(urlResolution || defaultResolution || 'Trending 7 Days');
      }
    } else {
      setSelectedResolution(resolutionCondition);
      setSelectedResolutionButton(resolutionCondition);
      setSelectedRange(urlResolution || defaultResolution || 'Trending 7 Days');
      setSelectedDate(
        urlDate && dayjs(urlDate).isValid()
          ? dayjs(urlDate).format('YYYY-MM-DD')
          : dayjs(dateFilter.defaultValue).format('YYYY-MM-DD'),
      );
      setInnerSelectedDate(
        urlDate && dayjs(urlDate).isValid()
          ? dayjs(urlDate).format('YYYY-MM-DD')
          : dayjs(dateFilter.defaultValue).format('YYYY-MM-DD'),
      );
      setOldPrismId(prismId);
    }
  }, [
    isTimeResolution,
    measure,
    resolutionOptions,
    oldPrismId,
    prismId,
    filters,
    defaultResolution,
    urlResolution,
    urlDate,
    resolutionCondition,
    dateFilter.defaultValue,
  ]);

  const handleClose = useCallback(() => {
    setIsHidden(true);
  }, []);

  return (
    <Wrapper>
      <DateTitle>Formation and date</DateTitle>
      <DateWrapper>
        {isTimeResolution ? (
          <DisplayDateResolutionTitle timeResolution={timeResolution} />
        ) : (
          <DisplayDateTitle measure={measure} />
        )}
        {isTimeResolution ? (
          <DisplayDateResolution
            value={selectedDate}
            onClick={() => setIsHidden(!isHidden)}
            timeResolution={timeResolution}
            filters={filters}
          />
        ) : (
          <DisplayDate value={selectedDate} onClick={() => setIsHidden(!isHidden)} measure={measure} />
        )}
        <DatePickerEditor
          name={name}
          value={innerSelectedDate}
          isHidden={isHidden}
          setIsHidden={setIsHidden}
          handleSave={handleSave}
          dateType={dateType}
          options={resolutionOptions || options}
          onDateChange={onDateChange}
          timeResolution={timeResolution}
          isTimeResolution={isTimeResolution}
          filters={filters}
          dateFilter={dateFilter}
          rangeValue={selectedRange}
          onRangeChange={setSelectedRange}
          resolutionValue={selectedResolutionButton}
          onResolutionChange={setSelectedResolutionButton}
          resolutionOptions={resolutionOptions || options}
        />
        <DatePickerBg hidden={isHidden ? 1 : 0} onClick={handleClose} />
      </DateWrapper>
    </Wrapper>
  );
};

export default DateFilterRange;
